import BoldSmallText from '@/components/BoldSmallText';
import PreviewSoldOut from '@/components/Preview/PreviewSoldOut';
import { REST_OF_THE_WORLD } from '@/constants';
import { unionCountries } from '@/constants/options';
import useLocationOptions from '@/hooks/useLocationOptions';
import { apiCaller } from '@/redux/query';
import { isSkipApiSelector } from '@/redux/slice/auth.slice';
import { selectedRuleSelector } from '@/redux/slice/preview.slice';
import { dataSettingsSelector } from '@/redux/slice/settings.slice';
import { ILanguageRule } from '@/types/languageRule.type';
import { IConditionValue, ILocationCondition, IRule } from '@/types/newSetup.type';
import { Divider } from '@shopify/polaris';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { useSelector } from 'react-redux';
import PreviewCountry from './PreviewCountry';
import PreviewMethod from './PreviewMethod';
import PreviewZipcode from './PreviewZipcode';
import ShippingFlow from './ShippingFlow';
import { DisplayModeEnum } from '@/constants/enum';
import { compareTime, createDateByTimezoneOffset, validateTime } from '@/helpers/time';

// Helper functions for processing rules
const applyLanguageRules = (rules: IRule[], languageRule?: ILanguageRule): IRule[] => {
  if (!languageRule?.ruleContents?.length) return rules;

  return rules.map((rule) => {
    const currentLanguageRuleContent = languageRule.ruleContents?.find((item) => item.id === rule.id);
    if (currentLanguageRuleContent) {
      const { name, ...rest } = currentLanguageRuleContent;
      return {
        ...rule,
        ...rest,
        styledName: name,
      };
    }
    return rule;
  });
};

const processCountryConditions = (rules: IRule[], countryOptions: { value: string; label: string }[]): IRule[] => {
  return rules.map((rule) => {
    const ruleHasCountryCondition = rule.locationConditions?.find((condition) => condition.type === 'country');
    if (!ruleHasCountryCondition) return rule;

    const newCountry = ruleHasCountryCondition.value.map((country) => {
      // Handle wildcard case
      if (country.data === '*') {
        return countryOptions.map((opt) => ({
          data: opt.value,
          countryStateName: opt.label,
        }));
      }

      // Handle region case
      const listCountryInRegion = unionCountries[country?.data as keyof typeof unionCountries];
      if (listCountryInRegion) {
        const temp: IConditionValue[] = [];
        listCountryInRegion.split(',').forEach((countryCode) => {
          const countryOption = countryOptions?.find((option) => option.value === countryCode);
          temp.push({
            data: countryCode,
            countryStateName: countryOption?.label,
          });
        });
        return temp;
      }

      // Return original country if no special handling needed
      return country;
    });

    // Update location conditions with processed countries
    const currentCountryIndex = rule.locationConditions?.findIndex((condition) => condition.type === 'country');
    if (currentCountryIndex !== -1) {
      const newLocationConditions = [...rule.locationConditions];
      newLocationConditions[currentCountryIndex] = {
        type: 'country',
        value: newCountry.flat(),
      } as ILocationCondition;

      return {
        ...rule,
        locationConditions: newLocationConditions,
      };
    }

    return rule;
  });
};

const handleRestOfWorldCondition = (rules: IRule[], countryOptions: { value: string; label: string }[]): IRule[] => {
  const hasRuleSelectRestOfWorld = rules.some((rule) =>
    rule.locationConditions.find(
      (condition) => condition.type === 'country' && condition.value.some((el) => el.data === REST_OF_THE_WORLD),
    ),
  );

  if (!hasRuleSelectRestOfWorld) return rules;

  // Find all countries already used in rules
  const countriesUsed = rules
    .flatMap((rule) => rule.locationConditions.find((condition) => condition.type === 'country')?.value || [])
    .filter((country) => !!country && country.data !== REST_OF_THE_WORLD);

  // Calculate "rest of the world" countries
  const restOfTheWorld = countryOptions
    ?.filter((option) => !countriesUsed.some((country) => country?.data === option.value))
    ?.map((option) => ({
      data: option.value,
      countryStateName: option.label,
    }));

  // Update rules that have REST_OF_THE_WORLD condition
  return rules.map((rule) => {
    const ruleHasRestOfTheWorld = rule.locationConditions?.find(
      (condition) => condition.type === 'country' && condition.value.some((el) => el.data === REST_OF_THE_WORLD),
    );

    if (!ruleHasRestOfTheWorld) return rule;

    const currentCountryIndex = rule.locationConditions?.findIndex((condition) => condition.type === 'country');
    if (currentCountryIndex !== -1) {
      const newLocationConditions = [...rule.locationConditions];
      newLocationConditions[currentCountryIndex] = {
        type: 'country',
        value: restOfTheWorld,
      } as ILocationCondition;

      return {
        ...rule,
        locationConditions: newLocationConditions,
      };
    }

    return rule;
  });
};

interface IProps {
  defaultData?: IRule;
  languageRule?: ILanguageRule;
  zipcodePlaceholder?: string;
  isAutoDetect?: boolean;
  checkScheduling?: boolean;
}

const PreviewRule = ({ defaultData, languageRule, zipcodePlaceholder, isAutoDetect, checkScheduling }: IProps) => {
  const dataSettings = useSelector(dataSettingsSelector);
  const selectedRule = useSelector(selectedRuleSelector);
  const locationOptions = useLocationOptions({
    countries: true,
  });

  const [selected, setSelected] = useState('');
  const isSkip = useSelector(isSkipApiSelector);
  const { data: rulesDataRes } = apiCaller.useGetRulesQuery(
    { page: 1, perPage: -1, sortBy: dataSettings.ruleSortBy },
    { skip: isSkip },
  );

  const getEnabledRules = useCallback(
    (rules?: IRule[]): IRule[] => {
      if (!rules?.length) return [];

      // Filter enabled rules
      let enabledRules = rules.filter((rule) => rule.enable);

      if (!checkScheduling) return enabledRules;

      // Filter rules based on scheduling
      return enabledRules.filter((rule) => {
        const { startDisplayDate, endDisplayDate, daySchedules, displayMode } = rule;
        const currentDate = createDateByTimezoneOffset(dataSettings.date_timezone_offset);

        // Handle specific days scheduling
        if (displayMode === DisplayModeEnum.SpecificDays) {
          const currentDaySchedule = daySchedules?.find((schedule) => schedule.weekday === currentDate.getDay());

          if (!currentDaySchedule) return true;
          if (!currentDaySchedule.enable) return false;

          const currentTime = `${currentDate.getHours()}:${currentDate.getMinutes()}`;
          if (
            !validateTime(currentTime) ||
            !validateTime(currentDaySchedule.startTime) ||
            !validateTime(currentDaySchedule.endTime)
          )
            return true;
          return (
            compareTime(currentTime, currentDaySchedule.startTime) >= 0 &&
            compareTime(currentTime, currentDaySchedule.endTime) <= 0
          );
        }

        // Handle date range scheduling
        if (displayMode === DisplayModeEnum.DateRange) {
          if (!startDisplayDate || !endDisplayDate) return true;

          const startTime = new Date(startDisplayDate).getTime();
          const endTime = new Date(endDisplayDate).getTime();
          const currentTime = currentDate.getTime();

          return currentTime >= startTime && currentTime <= endTime;
        }

        return true;
      });
    },
    [checkScheduling, dataSettings.date_timezone_offset],
  );

  const rulesData = useMemo(() => {
    const countryOptions = locationOptions?.countryOptions;

    // Get enabled rules
    let enableRules = defaultData ? [defaultData] : getEnabledRules(rulesDataRes?.data);

    // Apply language rules if available
    enableRules = applyLanguageRules(enableRules, languageRule);

    // Early return if no country options
    if (!countryOptions?.length) return enableRules;

    // Process country conditions
    const rulesWithProcessedCountries = processCountryConditions(enableRules, countryOptions);

    // Handle "Rest of the World" special case
    return handleRestOfWorldCondition(rulesWithProcessedCountries, countryOptions);
  }, [defaultData, getEnabledRules, languageRule, locationOptions?.countryOptions, rulesDataRes?.data]);

  const zipcodeRules = useMemo(() => {
    return rulesData.filter((rule) => rule.locationConditions?.find((condition) => condition.type === 'zipcode'));
  }, [rulesData]);

  const countryRules = useMemo(() => {
    return rulesData.filter((rule) => rule.locationConditions?.find((condition) => condition.type === 'country'));
  }, [rulesData]);

  useEffect(() => {
    if (defaultData) {
      const zipcodes = defaultData?.locationConditions?.find((condition) => condition.type === 'zipcode')?.value;
      const country = defaultData?.locationConditions?.find((condition) => condition.type === 'country')?.value;
      if (zipcodes?.length && zipcodes?.every((zipcode) => zipcode.data)) {
        setSelected('zipcode');
      } else if (country?.length && country?.every((country) => country.data)) {
        setSelected('location');
      } else {
        setSelected('');
      }
    } else {
      if (zipcodeRules.length > 0 && countryRules.length > 0) {
        setSelected('location');
      } else if (zipcodeRules.length > 0) {
        setSelected('zipcode');
      } else {
        setSelected('location');
      }
    }
  }, [zipcodeRules, countryRules, defaultData]);

  const { background_color: backgroundColor, text_color: textColor, text_size: textSize } = dataSettings;

  const isInCURule = useMemo(() => {
    return (
      defaultData?.locationConditions?.filter(
        (condition) => condition.value?.length && condition.value?.every((value) => value.data),
      )?.length === 0
    );
  }, [defaultData?.locationConditions]);

  return (
    <div>
      <div>
        {zipcodeRules.length > 0 && countryRules.length > 0 ? (
          <div className="ot-estimated-shipping-tabs">
            <button
              className={`ot-estimated-shipping-tab`}
              style={
                selected === 'location'
                  ? {
                      backgroundColor: backgroundColor,
                      fontSize: textSize + 'px',
                      color: textColor,
                    }
                  : {
                      backgroundColor: backgroundColor + '33',
                      fontSize: textSize + 'px',
                      color: textColor,
                    }
              }
              onClick={() => setSelected('location')}
            >
              Location
            </button>
            <button
              className={`ot-estimated-shipping-tab`}
              style={
                selected === 'zipcode'
                  ? {
                      backgroundColor: backgroundColor,
                      fontSize: textSize + 'px',
                      color: textColor,
                    }
                  : {
                      backgroundColor: backgroundColor + '33',
                      fontSize: textSize + 'px',
                      color: textColor,
                    }
              }
              onClick={() => setSelected('zipcode')}
            >
              Zipcode
            </button>
          </div>
        ) : null}
        {zipcodeRules.length > 0 && selected === 'zipcode' && (
          <PreviewZipcode rules={rulesData} zipcodePlaceholder={zipcodePlaceholder} isAutoDetect={isAutoDetect} />
        )}
        {countryRules.length > 0 && selected === 'location' && <PreviewCountry rules={rulesData} isAutoDetect={isAutoDetect} />}
        {((zipcodeRules.length === 0 && countryRules.length === 0) || isInCURule) && (
          <PreviewMethod rules={rulesData} isEmptyRule={rulesData.length === 0} />
        )}
      </div>

      <ShippingFlow
        orderToShipDays={defaultData ? defaultData?.minPrepDays : selectedRule?.minPrepDays || 0}
        prepDays={defaultData ? defaultData?.maxPrepDays : selectedRule?.maxPrepDays || 0}
        shipToReceiveDays={defaultData ? defaultData?.minDeliveryDays : selectedRule?.minDeliveryDays || 0}
        estDays={defaultData ? defaultData?.maxDeliveryDays : selectedRule?.maxDeliveryDays || 0}
        languageRule={languageRule}
        isUsePreOrderDate={(defaultData ? defaultData?.isUsePreOrderDate : selectedRule?.isUsePreOrderDate) || false}
        preOrderDate={(defaultData ? defaultData?.preOrderDate : selectedRule?.preOrderDate) || ''}
      />

      {defaultData?.soldOutEnabled && defaultData?.soldOutCustomTextEnabled && (
        <>
          <div className="mt-16 mb-16">
            <Divider />
          </div>
          <BoldSmallText>When out of stock</BoldSmallText>
          <PreviewSoldOut ruleSelected={defaultData} />
        </>
      )}
    </div>
  );
};

export default PreviewRule;
